'use strict';

import {createElWithAttrs} from '@borngroup/born-utilities';
import {subscribe} from '../helpers/pubSub';
import {addEventListenerList} from '../helpers/utilities';
import {appendParamsToURL} from '../helpers/urlUtilities';
import * as storeLocator from '../modules/module.store-locator.js';
import {getPidValue, getProductContainer, getQuantitySelector, getQuantitySelected} from '../product/productUtils';
import {setupVariationListeners} from '../product/variationsUtils';

const PRODUCT_CHANGE_EVENT = 'product:afterAttributeSelect.productAvailability';

/**
 * Updates the error message display for "not ready" products, when switching
 * between product variations while the Product Availability modal is open.
 */
function _updateErrorMessageVisibility(container, product) {
    let notReadyMsgEl = container.querySelector('[data-locator-component="not-ready"]'),
        notResultsMsgEl = container.querySelector('[data-locator-component="no-results"]');

    if (product.readyToOrder) {
        notReadyMsgEl.classList.add('hidden');
    } else {
        notResultsMsgEl.classList.add('hidden');
        notReadyMsgEl.classList.remove('hidden');
    }
}

/**
 * Hides the store's contact form whenever switching to a different product.
 */
function _hideContactForm(container) {
    let contactToggle = container.querySelector('[data-locator-component="availability-trigger"].set--show-form');

    if (contactToggle && contactToggle.toggle) {
        contactToggle.toggle.unset();
    }
}

/**
 * Attaches a listener to the `body` to align with SFRA's setup. This event is fired from `product/variationsUtils`.
 * When the event fires, request an update for the product store availability, by providing the updated product ID/Qty.
 * @return {[type]} [description]
 */
function _setupProductUpdateListener() {
    $('body').off(PRODUCT_CHANGE_EVENT).on(PRODUCT_CHANGE_EVENT, function(evt, response) {
        storeLocator.requestStoreSearch(appendParamsToURL(window.SFRA.Urls.storeLocatorFindStores, {renderView: 'productAvailability'}), {
            pid: response.data.product.id,
            products: `${response.data.product.id}:${getQuantitySelected(response.container)}`,
            radius: 20000,
            postalCode: '',
            typeCheck: 'CR'
        }, {
            locatorEl: response.container,
            isProductUpdate: true,
            productData: response.data.product
        });
    });
}

/**
 * Replaces the content in the modal window with find stores components and
 * the result store list.
 * @param {string} pid - The product ID to search for
 * @param {number} quantity - Number of products to search inventory for
 * @param {number} selectedPostalCode - The postal code to search for inventory
 * @param {number} selectedRadius - The radius to search for inventory
 */
export function showStoreLocator() {
    let payload = {
            pid: getPidValue(this),
            products: `${getPidValue(this)}:${getQuantitySelected(this)}`,
            radius: this.getAttribute('data-radius') || '',
            postalCode: this.getAttribute('data-postal') || ''
        },
        prompt = this.getAttribute('data-prompt-url') || true;

    storeLocator.requestStoreSearch(this.dataset.actionUrl, payload, {prompt, targetFormEl: getProductContainer(this), allowSearchAfterSelection: true});
}

/**
 * Remove the selected store.
 * @param {HTMLElement} container - the target html element
 */
export function deselectStore(container) {
    storeLocator.removeTargetFormData({targetFormEl: container});

    getQuantitySelector(container).removeAttribute('data-original-html');
}

/**
 * Updates the store pickup trigger disabled status.
 */
export function updateTriggerStatus(response, container) {
    let pickupTrigger = container.querySelector('[data-product-component="pickup-trigger"]');

    if (pickupTrigger) {
        // pickupTrigger.disabled = !response.product.readyToOrder || !response.product.available || !response.product.availableForStoreReservation;
        pickupTrigger.disabled = !response.product.availableForStoreReservation;

        response.product.availableForStoreReservation ? pickupTrigger.classList.remove('hidden') : pickupTrigger.classList.add('hidden');
    }
}

/**
 * Update quantity options. Only display quantity options that are available for the store.
 * @param {sring} searchPID - The product ID of the selected product.
 * @param {number} storeId - The store ID selected for in store pickup.
 */
export function requestInventoryCheck(productContainer, searchPID, storeId) {
    let atsURL = productContainer.querySelector('[data-product-component="pickup-trigger"]').dataset.atsActionUrl,
        qtyEl = getQuantitySelector(productContainer);

    //Update Quantity dropdown, Remove quantity greater than inventory
    $.ajax({
        url: atsURL,
        data: {
            pid: searchPID,
            quantitySelected: getQuantitySelected(productContainer),
            storeId: storeId
        },
        method: 'GET',
        success: function(response) {
            removeQuantityOptions(qtyEl, response.atsValue);

            $('body').trigger('storePickup:afterInventoryCheck', {
                data: response, container: productContainer
            });
        }
    });
}

/**
 * Updates the Quantity Selector based on the In Store Quantity.
 * @param {string} qtyEl - Quantity Element
 * @param {number} productAtsValue - Inventory in the selected store
 */
export function removeQuantityOptions(qtyEl, productAtsValue) {
    //Do not attempt to generate Quantity Options if product QTY cannot be updated by the user.
    if (window.SFRA.SitePreferences.allowProductLineItemRepeats) {
        return false;
    }

    let DOMSelectedValue = qtyEl.value;

    for (let i = qtyEl.options.length - 1; i >= productAtsValue && i > 0; i--) {
        qtyEl.removeChild(qtyEl.options[i]);
    }

    //Select the max possible value if the available stock is lower than the previously-selected qty.
    if (DOMSelectedValue > productAtsValue) {
        qtyEl.selectedIndex = qtyEl.options.length - 1;
    }
}

/**
 * Updates the Quantity Selector based on the In Store Quantity.
 * @param {string} qtyEl - Quantity Element
 * @param {number} productAtsValue - Inventory in the selected store
 */
export function addQuantityOptions(qtyEl, productAtsValue, responseSelectedValue) {
    //Do not attempt to generate Quantity Options if product QTY cannot be updated by the user.
    if (window.SFRA.SitePreferences.allowProductLineItemRepeats) {
        return false;
    }

    let DOMSelectedValue = qtyEl.value;

    for (let i = qtyEl.options.length + 1; i <= productAtsValue; i++) {
        createElWithAttrs(qtyEl, {value: i}, 'option', i);
    }

    //Select the max possible value if the available stock is lower than the previously-selected qty.
    if (DOMSelectedValue > productAtsValue) {
        qtyEl.selectedIndex = qtyEl.options.length - 1;
    } else if (responseSelectedValue) {
        qtyEl.value = responseSelectedValue;
    }
}

export default {
    /**
     * Handle store pickup option selection on PDP. Either displays a modal or shows the previously selected store data.
     */
    showInStoreInventory: function() {
        addEventListenerList('[data-product-component="pickup-trigger"]', 'click', function() {
            // if (this.checked) {
            //If there is already a selected store, display the store info and set the `selectedStoreInput` value again.
            if (this.dataset.hasSelectedStore) {
                let productContainer = getProductContainer(this),
                    selectedStoreDetails = productContainer.querySelector('[data-locator-component="selected-store-display"]'),
                    selectedStoreInput = storeLocator.getSelectedStoreInput(productContainer);

                selectedStoreInput.value = this.dataset.hasSelectedStore;
                selectedStoreDetails.classList.remove('hidden');

                requestInventoryCheck(productContainer, getPidValue(productContainer), this.dataset.hasSelectedStore);
            } else {
                //If there's no selection yet, load the store selection modal.
                showStoreLocator.call(this);
            }
            // }
        });
    },

    selectStoreWithInventory: function() {
        subscribe('storeLocator:confirmed', function(data) {
            let pickupChangeEl = data.locatorSettings.targetFormEl.querySelector('[data-product-component="pickup-change"]'),
                pickupTriggerEl = data.locatorSettings.targetFormEl.querySelector('[data-product-component="pickup-trigger"]');

            pickupChangeEl.dataset.radius = data.storeData.searchRadius;
            pickupChangeEl.dataset.postal = data.storeData.searchPostalCode;
            pickupTriggerEl.dataset.hasSelectedStore = data.storeData.storeID;

            requestInventoryCheck(data.locatorSettings.targetFormEl, getPidValue(data.locatorSettings.targetFormEl), data.storeData.storeID);
        });
    },

    changeStore: function() {
        addEventListenerList('[data-product-component="pickup-change"]', 'click', showStoreLocator);
    },

    /**
     * Listen for module.store-locator's store list changes.
     * Re-bind the necessary events whenever the list of stores is updated, so that data is properly passed to the "store reservation" form.
     */
    onStoreListChange: function() {
        subscribe('storeLocator:updateRendered', function(data) {
            let locatorContainer = data.locatorSettings.locatorEl;

            if (locatorContainer) {
                let checkAvailabilityTriggers = locatorContainer.querySelectorAll('[data-locator-component="availability-trigger"]'),
                    inputPID = locatorContainer.querySelector('[data-locator-component="input-pid"]'),
                    inputBoutiqueID = locatorContainer.querySelector('[data-locator-component="input-boutiqueID"]'),
                    productAvailabilityContainer = locatorContainer.querySelector('[data-product-container="productAvailability"]');

                addEventListenerList(checkAvailabilityTriggers, 'click', function() {
                    inputPID.value = this.getAttribute('data-pid');
                    inputBoutiqueID.value = this.getAttribute('data-store-id');
                });

                if (productAvailabilityContainer) {
                    setupVariationListeners(productAvailabilityContainer);

                    _setupProductUpdateListener();
                }
            }
        });
    },

    onStoreUpdateRequest: function() {
        subscribe('storeLocator:updateRequested', function(data) {
            if (data.locatorSettings.isProductUpdate) {
                _updateErrorMessageVisibility(data.locatorSettings.locatorEl, data.locatorSettings.productData);
                _hideContactForm(data.locatorSettings.locatorEl);
            }
        });
    },

    /**
     * Listen to when the store selection modal is closed, and if nothing was selected, reset the delivery option to the default ("Home").
     * @return {[type]} [description]
     */
    closeStoreSelection: function() {
        subscribe('storeLocator:closed', function() {
            // let pickupRemoveEl = data.locatorSettings.targetFormEl.querySelector('[data-product-component="pickup-remove"]'),
            //     pickupTriggerEl = data.locatorSettings.targetFormEl.querySelector('[data-product-component="pickup-trigger"]');

            // if (!pickupTriggerEl.dataset.hasSelectedStore) {
            //     pickupRemoveEl.checked = true;
            // }

            $('body').off(PRODUCT_CHANGE_EVENT);
        });
    }
};
